import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Handshake, ArrowLeft, Users, Check, X, Trash2, Calendar, Plus, Edit3, Clock, AlertCircle,
  MessageCircle, Link as LinkIcon, QrCode, Download,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import EventoColetivoForm from '../components/EventoColetivoForm';
import ColetivoCalendar from '../components/ColetivoCalendar';
import ImageUploader from '../components/ImageUploader';
import { toast } from 'sonner';

const STATUS_BADGE = {
  ativo: { label: 'Ativo', cls: 'bg-green-100 text-green-700' },
  pendente_analise: { label: 'Em análise', cls: 'bg-amber-100 text-amber-700' },
  rejeitado: { label: 'Rejeitado', cls: 'bg-red-100 text-red-700' },
  inativo: { label: 'Inativo', cls: 'bg-gray-100 text-gray-600' },
  rascunho: { label: 'Rascunho', cls: 'bg-yellow-100 text-yellow-700' },
};

export default function ResponsavelColetivos() {
  const navigate = useNavigate();
  const [meus, setMeus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/v2/coletivos/responsavel/meus')
      .then((r) => setMeus(r.data || []))
      .catch(() => setMeus([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando…</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="responsavel-coletivos-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Handshake className="w-6 h-6 text-purple-600" />
            Meus coletivos
          </h1>
          <Button onClick={() => navigate('/coletivos/cadastrar')} data-testid="btn-novo-coletivo-resp">
            <Plus className="w-4 h-4 mr-1" /> Cadastrar coletivo
          </Button>
        </div>
        {meus.length === 0 ? (
          <div className="bg-white p-8 rounded-xl text-center border border-gray-100">
            <p className="text-gray-600">Você ainda não é responsável por nenhum coletivo.</p>
            <p className="text-xs text-gray-500 mt-2 mb-4">
              Solicite o cadastro do seu coletivo (feira, cooperativa, grupo de artesãos...) —
              após a aprovação da gestão municipal, você gerencia membros e eventos por aqui.
            </p>
            <Button onClick={() => navigate('/coletivos/cadastrar')} data-testid="btn-cadastrar-coletivo-empty">
              <Plus className="w-4 h-4 mr-1" /> Cadastrar coletivo
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {meus.map((c) => {
              const badge = STATUS_BADGE[c.status] || STATUS_BADGE.rascunho;
              const isAtivo = c.status === 'ativo';
              const CardInner = (
                <>
                  {c.foto_capa && <div className="h-24 bg-cover bg-center" style={{ backgroundImage: `url(${c.foto_capa})` }} />}
                  <div className="p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold">{c.nome}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`} data-testid={`coletivo-status-${c.id}`}>
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{c.descricao}</p>
                    {c.status === 'pendente_analise' && (
                      <div className="mt-3 p-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-800 flex items-start gap-1.5">
                        <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        Aguardando análise da gestão municipal. Você será avisado por e-mail.
                      </div>
                    )}
                    {c.status === 'rejeitado' && c.motivo_status && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700 flex items-start gap-1.5" data-testid={`coletivo-motivo-${c.id}`}>
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span><strong>Motivo:</strong> {c.motivo_status}</span>
                      </div>
                    )}
                    {isAtivo && (
                      <div className="mt-3 flex items-center gap-3 text-xs text-gray-600">
                        <span><strong>{c.total_membros_aprovados || 0}</strong> aprovados</span>
                        {c.total_pendentes > 0 && (
                          <span className="text-amber-600 font-medium">⏳ {c.total_pendentes} pendentes</span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
              return isAtivo ? (
                <Link
                  key={c.id}
                  to={`/responsavel/coletivos/${c.id}`}
                  data-testid={`resp-card-${c.id}`}
                  className="block bg-white rounded-xl border border-gray-100 hover:border-purple-300 hover:shadow-md transition overflow-hidden"
                >
                  {CardInner}
                </Link>
              ) : (
                <div
                  key={c.id}
                  data-testid={`resp-card-${c.id}`}
                  className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                >
                  {CardInner}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Página de gestão individual ============
export function ResponsavelColetivoGestao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coletivo, setColetivo] = useState(null);
  const [membros, setMembros] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('membros');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/v2/coletivos/${id}/gestao`);
      setColetivo(r.data);
      setMembros(r.data.membros || []);
      const re = await api.get(`/v2/coletivos/${r.data.slug}/eventos`, { params: { only_future: false } });
      setEventos(re.data || []);
    } catch {
      setColetivo(null);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  async function aprovar(bizId) {
    try { await api.post(`/v2/coletivos/${id}/membros/${bizId}/aprovar`); toast.success('Aprovado'); reload(); }
    catch { toast.error('Erro'); }
  }
  async function rejeitar(bizId) {
    const motivo = prompt('Motivo (opcional):') || '';
    try { await api.post(`/v2/coletivos/${id}/membros/${bizId}/rejeitar`, { motivo }); toast.success('Rejeitado'); reload(); }
    catch { toast.error('Erro'); }
  }
  async function remover(bizId) {
    if (!confirm('Remover membro?')) return;
    try { await api.post(`/v2/coletivos/${id}/membros/${bizId}/remover`); toast.success('Removido'); reload(); }
    catch { toast.error('Erro'); }
  }

  if (loading) return <div className="p-12 text-center text-gray-500">Carregando…</div>;
  if (!coletivo) return <div className="p-12 text-center"><p>Acesso negado ou coletivo não encontrado.</p></div>;

  const pendentes = membros.filter((m) => m.status === 'pendente');
  const aprovados = membros.filter((m) => m.status === 'aprovado');

  return (
    <ResponsavelColetivoGestaoView
      coletivo={coletivo}
      pendentes={pendentes}
      aprovados={aprovados}
      eventos={eventos}
      tab={tab}
      setTab={setTab}
      navigate={navigate}
      aprovar={aprovar}
      rejeitar={rejeitar}
      remover={remover}
      reload={reload}
      id={id}
    />
  );
}

function ResponsavelColetivoGestaoView({ coletivo, pendentes, aprovados, eventos, tab, setTab, navigate, aprovar, rejeitar, remover, reload, id }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const qrRef = React.useRef(null);

  async function gerarConvite() {
    setInviteLoading(true);
    try {
      const r = await api.post(`/v2/coletivos/${id}/convite`);
      const url = `${window.location.origin}/coletivos/convite/${r.data.token}`;
      setInviteUrl(url);
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar convite');
      return null;
    } finally {
      setInviteLoading(false);
    }
  }

  async function convidarWhatsApp() {
    const url = inviteUrl || await gerarConvite();
    if (!url) return;
    const msg = `Olá! Você foi convidado(a) a participar do coletivo *${coletivo.nome}*. ` +
      `Entre com 1 clique pelo link: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  }

  async function copiarConvite() {
    const url = inviteUrl || await gerarConvite();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link de convite copiado!');
    } catch {
      toast.info(url);
    }
  }

  async function mostrarQr() {
    if (!inviteUrl) {
      const url = await gerarConvite();
      if (!url) return;
    }
    setShowQr((v) => !v);
  }

  function baixarQr() {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `convite-${coletivo.slug || coletivo.id}.png`;
    a.click();
    toast.success('QR Code baixado! Imprima e cole na banca ou no ponto do coletivo.');
  }

  async function removeEvento(eid) {
    if (!confirm('Remover este evento? Isso afeta toda a recorrência se for um evento recorrente.')) return;
    try {
      await api.delete(`/v2/coletivos/${id}/eventos/${eid}`);
      toast.success('Evento removido');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao remover');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="responsavel-coletivo-gestao">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button variant="outline" size="sm" onClick={() => navigate('/responsavel/coletivos')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <h1 className="text-2xl font-bold mb-2">{coletivo.nome}</h1>
        <p className="text-sm text-gray-500 mb-6">{coletivo.descricao}</p>

        <div className="flex gap-2 mb-6 border-b">
          {[
            { v: 'membros', l: `Membros (${aprovados.length}${pendentes.length > 0 ? ` · ${pendentes.length}⌛` : ''})` },
            { v: 'eventos', l: `Eventos (${eventos.length})` },
            { v: 'agenda', l: 'Agenda' },
            { v: 'fotos', l: 'Fotos' },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`resp-tab-${t.v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                tab === t.v ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >{t.l}</button>
          ))}
        </div>

        {tab === 'membros' && (
          <div className="space-y-6">
            {/* Convite via WhatsApp (Iter167) */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5" data-testid="convite-box">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex-1 min-w-[220px]">
                  <h2 className="font-bold text-gray-900 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    Convide membros pelo WhatsApp
                  </h2>
                  <p className="text-xs text-gray-600 mt-1">
                    Quem entrar pelo link é aprovado automaticamente — cada feirante/artesão entra com 1 clique.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={convidarWhatsApp}
                    disabled={inviteLoading}
                    data-testid="btn-convidar-whatsapp"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Enviar no WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={copiarConvite} disabled={inviteLoading} data-testid="btn-copiar-convite">
                    <LinkIcon className="w-4 h-4 mr-1" />
                    Copiar link
                  </Button>
                  <Button size="sm" variant="outline" onClick={mostrarQr} disabled={inviteLoading} data-testid="btn-qr-convite">
                    <QrCode className="w-4 h-4 mr-1" />
                    QR Code
                  </Button>
                </div>
              </div>
              {inviteUrl && (
                <p className="mt-3 text-xs bg-white border border-green-200 rounded-lg px-3 py-2 text-gray-700 break-all" data-testid="convite-url">
                  {inviteUrl}
                </p>
              )}
              {showQr && inviteUrl && (
                <div className="mt-4 flex flex-col sm:flex-row items-center gap-4 bg-white border border-green-200 rounded-xl p-4" data-testid="convite-qr-box">
                  <div ref={qrRef} className="bg-white p-2 rounded-lg border border-gray-100">
                    <QRCodeCanvas value={inviteUrl} size={180} level="M" includeMargin />
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-sm font-semibold text-gray-900">QR Code do convite</p>
                    <p className="text-xs text-gray-600 mt-1 max-w-xs">
                      Baixe, imprima e cole na banca da feira ou no ponto do coletivo —
                      quem escanear entra direto no coletivo.
                    </p>
                    <Button size="sm" variant="outline" className="mt-3" onClick={baixarQr} data-testid="btn-baixar-qr">
                      <Download className="w-4 h-4 mr-1" />
                      Baixar PNG
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h2 className="font-bold mb-3 flex items-center gap-2"><Users className="w-4 h-4"/> Pendentes ({pendentes.length})</h2>
              {pendentes.length === 0 ? <p className="text-sm text-gray-500">Sem solicitações pendentes</p> : (
                <div className="space-y-2">
                  {pendentes.map((m) => (
                    <div key={m.negocio_id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`resp-pend-${m.negocio_id}`}>
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">{(m.negocio_nome||'?').charAt(0)}</div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{m.negocio_nome}</div>
                        <div className="text-xs text-gray-500">{m.negocio_categoria}</div>
                      </div>
                      <Button size="sm" onClick={() => aprovar(m.negocio_id)}><Check className="w-4 h-4 mr-1"/>Aprovar</Button>
                      <Button size="sm" variant="outline" onClick={() => rejeitar(m.negocio_id)}><X className="w-4 h-4"/></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h2 className="font-bold mb-3">Aprovados ({aprovados.length})</h2>
              <div className="space-y-2">
                {aprovados.map((m) => (
                  <div key={m.negocio_id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`resp-aprov-${m.negocio_id}`}>
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">{(m.negocio_nome||'?').charAt(0)}</div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{m.negocio_nome}</div>
                      <div className="text-xs text-gray-500">{m.negocio_categoria}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => remover(m.negocio_id)}><Trash2 className="w-4 h-4"/></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'eventos' && (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold flex items-center gap-2"><Calendar className="w-4 h-4"/> Eventos do coletivo</h2>
              <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="resp-novo-evento">
                <Plus className="w-4 h-4 mr-1"/> Novo evento
              </Button>
            </div>
            {eventos.length === 0 ? (
              <p className="text-sm text-gray-500">Ainda não há eventos vinculados.</p>
            ) : (
              <div className="space-y-2">
                {eventos.map((e) => (
                  <div key={e.id} className="p-3 rounded-lg border flex items-center gap-3" data-testid={`resp-evento-${e.id}`}>
                    <Calendar className="w-5 h-5 text-purple-600"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{e.titulo}</div>
                      <div className="text-xs text-gray-500">
                        {e.recorrencia?.tipo && e.recorrencia.tipo !== 'none' ? (
                          <span className="text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded text-[10px] uppercase mr-1">
                            {e.recorrencia.tipo}
                          </span>
                        ) : null}
                        {e.data_inicio} {e.hora_inicio && `· ${e.hora_inicio}`}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(e); setShowForm(true); }} data-testid={`resp-evento-edit-${e.id}`}>
                      <Edit3 className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => removeEvento(e.id)} data-testid={`resp-evento-del-${e.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'agenda' && (
          <ColetivoCalendar slug={coletivo.slug} />
        )}

        {tab === 'fotos' && (
          <div className="space-y-4" data-testid="resp-tab-fotos-content">
            <p className="text-sm text-gray-600">
              Coletivos com foto atraem mais membros e visitantes. Envie uma capa (horizontal) e uma foto de perfil (quadrada).
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <ImageUploader
                area="coletivo"
                tipo="capa"
                entidadeId={id}
                municipioId={coletivo.municipio_id}
                imagensAtuais={coletivo.imagens?.capa ? [coletivo.imagens.capa] : []}
                single
                label="Foto de capa"
                description="Imagem horizontal exibida no topo da página do coletivo"
                onChange={reload}
              />
              <ImageUploader
                area="coletivo"
                tipo="perfil"
                entidadeId={id}
                municipioId={coletivo.municipio_id}
                imagensAtuais={coletivo.imagens?.perfil ? [coletivo.imagens.perfil] : []}
                single
                label="Foto de perfil"
                description="Logo ou avatar do coletivo (quadrada)"
                onChange={reload}
              />
            </div>
          </div>
        )}

        {showForm && (
          <EventoColetivoForm
            coletivoId={id}
            evento={editing}
            onClose={() => { setShowForm(false); setEditing(null); }}
            onSaved={reload}
          />
        )}
      </div>
    </div>
  );
}
